import React, { useState, useContext, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { Toast } from 'primereact/toast';
import { AppContext } from '../AppContext';

function CustomerListPage() {
  const { customers, setCustomers, setSelectedCustomerId } = useContext(AppContext);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [isCreateDialogVisible, setIsCreateDialogVisible] = useState(false);
  const [isEditDialogVisible, setIsEditDialogVisible] = useState(false);
  const [newCustomer, setNewCustomer] = useState({
    accountName: '',
    url: '',
    phone: '',
    customerContact: { name: '', email: '' },
  });
  const [editingCustomer, setEditingCustomer] = useState(null);

  const navigate = useNavigate();
  const toast = useRef(null);

  const createNewCustomer = () => {
    const id = Date.now().toString();
    const customer = {
      ...newCustomer,
      id,
      syncStatus: 'local',
      serviceData: {}, // always initialize empty object
    };

    setCustomers((prev) => [...prev, customer]);
    setIsCreateDialogVisible(false);
    setNewCustomer({
      accountName: '',
      url: '',
      phone: '',
      customerContact: { name: '', email: '' },
    });
    toast.current?.show({
      severity: 'success',
      summary: 'Created',
      detail: 'Customer created locally',
    });
  };

  // Update an existing customer
  const updateCustomer = () => {
    setCustomers((prev) =>
      prev.map((cust) => (cust.id === editingCustomer.id ? editingCustomer : cust))
    );
    setIsEditDialogVisible(false);
    setEditingCustomer(null);
    toast.current?.show({
      severity: 'success',
      summary: 'Updated',
      detail: 'Customer updated locally',
    });
  };

  // Single button to navigate to service config page for selected customer
  const createCustomerDesign = () => {
    if (selectedCustomer) {
      setSelectedCustomerId(selectedCustomer.id);
      navigate(`/services/${selectedCustomer.id}`);
    }
  };

  const deleteCustomer = () => {
    if (selectedCustomer) {
      setCustomers((prev) =>
        prev.filter((cust) => cust.id !== selectedCustomer.id)
      );
      setSelectedCustomer(null);
      toast.current?.show({
        severity: 'info',
        summary: 'Deleted',
        detail: 'Customer deleted',
      });
    }
  };

  // Row-level edit button template
  const editRowTemplate = (rowData) => (
    <Button
      icon="pi pi-pencil"
      className="p-button-text p-button-rounded"
      onClick={() => {
        setEditingCustomer(rowData);
        setIsEditDialogVisible(true);
      }}
      tooltip="Edit Customer"
    />
  );

  const syncStatusBody = (rowData) => (
    <span>
      {rowData.syncStatus === 'synced' ? '✅ Synced' : '⚠️ Local Only'}
    </span>
  );

  return (
    <div className="p-4">
      <Toast ref={toast} />

      <Button
        label="Create New Customer"
        onClick={() => setIsCreateDialogVisible(true)}
        className="p-button-success mb-4"
      />

      <DataTable
        value={customers}
        selectionMode="single"
        selection={selectedCustomer}
        onSelectionChange={(e) => setSelectedCustomer(e.value)}
        dataKey="id"
        className="p-datatable-striped"
      >
        <Column field="accountName" header="Account Name" sortable />
        <Column field="url" header="URL" />
        <Column field="phone" header="Phone" />
        <Column field="syncStatus" header="Sync Status" body={syncStatusBody} />
        <Column field="dealName" header="Deal Name" />
        <Column header="Actions" body={editRowTemplate} style={{ textAlign: 'center', width: '100px' }} />
      </DataTable>

      <div className="mt-4 flex gap-2">
        {selectedCustomer && (
          <Button
            label="Create Customer Design"
            onClick={createCustomerDesign}
            className="p-button-primary"
          />
        )}
        <Button
          label="Delete Customer"
          disabled={!selectedCustomer}
          onClick={deleteCustomer}
          className="p-button-danger"
        />
      </div>

      {/* Create New Customer Dialog */}
      <Dialog
        header="Create New Customer"
        visible={isCreateDialogVisible}
        onHide={() => setIsCreateDialogVisible(false)}
        style={{ width: '50vw' }}
      >
        <div className="p-4">
          <div className="p-field mb-4">
            <label htmlFor="accountName">Account Name</label>
            <InputText
              id="accountName"
              value={newCustomer.accountName}
              onChange={(e) =>
                setNewCustomer({ ...newCustomer, accountName: e.target.value })
              }
              className="w-full"
            />
          </div>
          <div className="p-field mb-4">
            <label htmlFor="contactName">Contact Name</label>
            <InputText
              id="contactName"
              value={newCustomer.contactName}
              onChange={(e) =>
                setNewCustomer({ ...newCustomer, contactName: e.target.value })
              }
              className="w-full"
            />
          </div>
          <div className="p-field mb-4">
            <label htmlFor="partnerContact">Partner Contact</label>
            <InputText
              id="partnerContact"
              value={newCustomer.partnerContact}
              onChange={(e) =>
                setNewCustomer({ ...newCustomer, partnerContact: e.target.value })
              }
              className="w-full"
            />
          </div>
          <div className="p-field mb-4">
            <label htmlFor="parentAccountName">Parent Account Name</label>
            <InputText
              id="parentAccountName"
              value={newCustomer.parentAccountName}
              onChange={(e) =>
                setNewCustomer({ ...newCustomer, parentAccountName: e.target.value })
              }
              className="w-full"
            />
          </div>
          <div className="p-field mb-4">
            <label htmlFor="dealOwner">Deal Owner</label>
            <InputText
              id="dealOwner"
              value={newCustomer.dealOwner}
              onChange={(e) =>
                setNewCustomer({ ...newCustomer, dealOwner: e.target.value })
              }
              className="w-full"
            />
          </div>
          <div className="p-field mb-4">
            <label htmlFor="dealId">Deal ID</label>
            <InputText
              id="dealId"
              value={newCustomer.dealId}
              onChange={(e) =>
                setNewCustomer({ ...newCustomer, dealId: e.target.value })
              }
              className="w-full"
            />
          </div>
          <div className="p-field mb-4">
            <label htmlFor="dealName">Deal Name</label>
            <InputText
              id="dealName"
              value={newCustomer.dealName}
              onChange={(e) =>
                setNewCustomer({ ...newCustomer, dealName: e.target.value })
              }
              className="w-full"
            />
          </div>
          <div className="p-field mb-4">
            <label htmlFor="url">URL</label>
            <InputText
              id="url"
              value={newCustomer.url}
              onChange={(e) =>
                setNewCustomer({ ...newCustomer, url: e.target.value })
              }
              className="w-full"
            />
          </div>
          <div className="p-field mb-4">
            <label htmlFor="phone">Phone</label>
            <InputText
              id="phone"
              value={newCustomer.phone}
              onChange={(e) =>
                setNewCustomer({ ...newCustomer, phone: e.target.value })
              }
              className="w-full"
            />
          </div>
          <div className="p-field mb-4">
            <label>Customer Contact Name</label>
            <InputText
              value={newCustomer.customerContact.name}
              onChange={(e) =>
                setNewCustomer({
                  ...newCustomer,
                  customerContact: { ...newCustomer.customerContact, name: e.target.value },
                })
              }
              className="w-full"
            />
          </div>
          <div className="p-field mb-4">
            <label>Customer Contact Email</label>
            <InputText
              value={newCustomer.customerContact.email}
              onChange={(e) =>
                setNewCustomer({
                  ...newCustomer,
                  customerContact: { ...newCustomer.customerContact, email: e.target.value },
                })
              }
              className="w-full"
            />
          </div>
          <Button
            label="Save"
            onClick={createNewCustomer}
            className="p-button-success"
          />
        </div>
      </Dialog>

      {/* Edit Customer Dialog */}
      <Dialog
        header="Edit Customer"
        visible={isEditDialogVisible}
        onHide={() => {
          setIsEditDialogVisible(false);
          setEditingCustomer(null);
        }}
        style={{ width: '50vw' }}
      >
        {editingCustomer && (
          <div className="p-4">
            <div className="p-field mb-4">
              <label htmlFor="editAccountName">Account Name</label>
              <InputText
                id="editAccountName"
                value={editingCustomer.accountName || ''}
                onChange={(e) =>
                  setEditingCustomer({ ...editingCustomer, accountName: e.target.value })
                }
                className="w-full"
              />
            </div>
            <div className="p-field mb-4">
              <label htmlFor="editContactName">Contact Name</label>
              <InputText
                id="editContactName"
                value={editingCustomer.contactName || ''}
                onChange={(e) =>
                  setEditingCustomer({ ...editingCustomer, contactName: e.target.value })
                }
                className="w-full"
              />
            </div>
            <div className="p-field mb-4">
              <label htmlFor="editPartnerContact">Partner Contact</label>
              <InputText
                id="editPartnerContact"
                value={editingCustomer.partnerContact || ''}
                onChange={(e) =>
                  setEditingCustomer({ ...editingCustomer, partnerContact: e.target.value })
                }
                className="w-full"
              />
            </div>
            <div className="p-field mb-4">
              <label htmlFor="editParentAccountName">Parent Account Name</label>
              <InputText
                id="editParentAccountName"
                value={editingCustomer.parentAccountName || ''}
                onChange={(e) =>
                  setEditingCustomer({ ...editingCustomer, parentAccountName: e.target.value })
                }
                className="w-full"
              />
            </div>
            <div className="p-field mb-4">
              <label htmlFor="editDealOwner">Deal Owner</label>
              <InputText
                id="editDealOwner"
                value={editingCustomer.dealOwner || ''}
                onChange={(e) =>
                  setEditingCustomer({ ...editingCustomer, dealOwner: e.target.value })
                }
                className="w-full"
              />
            </div>
            <div className="p-field mb-4">
              <label htmlFor="editDealId">Deal ID</label>
              <InputText
                id="editDealId"
                value={editingCustomer.dealId || ''}
                onChange={(e) =>
                  setEditingCustomer({ ...editingCustomer, dealId: e.target.value })
                }
                className="w-full"
              />
            </div>
            <div className="p-field mb-4">
              <label htmlFor="editDealName">Deal Name</label>
              <InputText
                id="editDealName"
                value={editingCustomer.dealName || ''}
                onChange={(e) =>
                  setEditingCustomer({ ...editingCustomer, dealName: e.target.value })
                }
                className="w-full"
              />
            </div>
            <div className="p-field mb-4">
              <label htmlFor="editUrl">URL</label>
              <InputText
                id="editUrl"
                value={editingCustomer.url || ''}
                onChange={(e) =>
                  setEditingCustomer({ ...editingCustomer, url: e.target.value })
                }
                className="w-full"
              />
            </div>
            <div className="p-field mb-4">
              <label htmlFor="editPhone">Phone</label>
              <InputText
                id="editPhone"
                value={editingCustomer.phone || ''}
                onChange={(e) =>
                  setEditingCustomer({ ...editingCustomer, phone: e.target.value })
                }
                className="w-full"
              />
            </div>
            <div className="p-field mb-4">
              <label>Edit Customer Contact Name</label>
              <InputText
                value={editingCustomer.customerContact?.name || ''}
                onChange={(e) =>
                  setEditingCustomer({
                    ...editingCustomer,
                    customerContact: { ...editingCustomer.customerContact, name: e.target.value },
                  })
                }
                className="w-full"
              />
            </div>
            <div className="p-field mb-4">
              <label>Edit Customer Contact Email</label>
              <InputText
                value={editingCustomer.customerContact?.email || ''}
                onChange={(e) =>
                  setEditingCustomer({
                    ...editingCustomer,
                    customerContact: { ...editingCustomer.customerContact, email: e.target.value },
                  })
                }
                className="w-full"
              />
            </div>

            <Button label="Save" onClick={updateCustomer} className="p-button-success" />
          </div>
        )}
      </Dialog>
    </div>
  );
}

export default CustomerListPage;
